import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  FlipHorizontal,
  Info,
} from "lucide-react";

const RotateMatrixVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "3x3 Square",
      matrix: [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9],
      ],
    },
    {
      id: 2,
      name: "4x4 Square",
      matrix: [
        [5, 1, 9, 11],
        [2, 4, 8, 10],
        [13, 3, 6, 7],
        [15, 14, 12, 16],
      ],
    },
    {
      id: 3,
      name: "2x2 Square",
      matrix: [
        [1, 2],
        [3, 4],
      ],
    },
    {
      id: 4,
      name: "Single Element",
      matrix: [[1]],
    },
    {
      id: 5,
      name: "5x5 with Negatives",
      matrix: [
        [1, 2, 3, 4, 5],
        [6, 7, 8, 9, -1],
        [10, 11, 12, 13, -2],
        [14, 15, 16, 17, -3],
        [18, 19, 20, 21, -4],
      ],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [matrix, setMatrix] = useState(defaultTestCases[0].matrix);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function rotate(matrix):", indent: 0 },
    { line: "N = matrix.length", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Step 1: Transpose the matrix", indent: 1 },
    { line: "for i = 0 to N-1:", indent: 1 },
    { line: "for j = i+1 to N-1:", indent: 2 },
    { line: "swap(matrix[i][j], matrix[j][i])", indent: 3 },
    { line: "", indent: 0 },
    { line: "// Step 2: Reverse each row", indent: 1 },
    { line: "for i = 0 to N-1:", indent: 1 },
    { line: "reverse(matrix[i])", indent: 2 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (inputMatrix) => {
    const allSteps = [];
    let stepCount = 0;
    const N = inputMatrix.length;
    if (N === 0) return [];
    let currentMatrix = JSON.parse(JSON.stringify(inputMatrix));

    const addStep = (
      matrixState,
      description,
      highlightLine,
      pointers = {},
      phase = ""
    ) => {
      allSteps.push({
        step: stepCount++,
        matrix: JSON.parse(JSON.stringify(matrixState)),
        description,
        highlightLine,
        pointers,
        phase,
      });
    };

    addStep(currentMatrix, "Initial matrix.", 0, {}, "initial");

    // Phase 1: Transpose
    addStep(
      currentMatrix,
      "Step 1: Transpose the matrix across the diagonal.",
      3,
      {},
      "transpose"
    );
    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        addStep(
          currentMatrix,
          `Swapping [${i}][${j}] (${currentMatrix[i][j]}) with [${j}][${i}] (${currentMatrix[j][i]}).`,
          6,
          { swap1: { r: i, c: j }, swap2: { r: j, c: i } },
          "transpose"
        );
        [currentMatrix[i][j], currentMatrix[j][i]] = [
          currentMatrix[j][i],
          currentMatrix[i][j],
        ];
        addStep(
          currentMatrix,
          "Swap complete.",
          6,
          { swap1: { r: i, c: j }, swap2: { r: j, c: i } },
          "transpose"
        );
      }
    }
    addStep(currentMatrix, "Transpose complete.", 3, {}, "transpose_done");

    // Phase 2: Reverse rows
    addStep(currentMatrix, "Step 2: Reverse each row.", 9, {}, "reverse");
    for (let i = 0; i < N; i++) {
      addStep(currentMatrix, `Reversing row ${i}.`, 10, { row: i }, "reverse");
      let left = 0,
        right = N - 1;
      while (left < right) {
        addStep(
          currentMatrix,
          `Swapping ${currentMatrix[i][left]} and ${currentMatrix[i][right]}.`,
          10,
          { row: i, left, right },
          "reverse"
        );
        [currentMatrix[i][left], currentMatrix[i][right]] = [
          currentMatrix[i][right],
          currentMatrix[i][left],
        ];
        addStep(
          currentMatrix,
          "Swap complete.",
          10,
          { row: i, left, right },
          "reverse"
        );
        left++;
        right--;
      }
      addStep(currentMatrix, `Row ${i} reversed.`, 10, { row: i }, "reverse");
    }

    allSteps.push({
      step: stepCount++,
      matrix: JSON.parse(JSON.stringify(currentMatrix)),
      description: "Rotation complete. Final matrix shown.",
      highlightLine: -1,
      pointers: {},
      phase: "done",
      isFinal: true,
    });

    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(matrix);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [matrix]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setMatrix(testCases[index].matrix);
  };

  const addCustomTestCase = () => {
    try {
      const parsedMatrix = JSON.parse(customInput);
      if (
        !Array.isArray(parsedMatrix) ||
        !parsedMatrix.every(
          (row) =>
            Array.isArray(row) && row.every((cell) => typeof cell === "number")
        )
      ) {
        throw new Error("Invalid format.");
      }
      if (
        parsedMatrix.length > 0 &&
        !parsedMatrix.every((row) => row.length === parsedMatrix.length)
      ) {
        throw new Error("Matrix must be square (N x N).");
      }
      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 4}`,
        matrix: parsedMatrix,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomInput("");
    } catch (error) {
      alert("Invalid format. Use a JSON 2D square array like [[1,2],[3,4]].");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <FlipHorizontal className="text-blue-500" size={24} />
        Rotate Image by 90 Degrees
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Given an n x n 2D matrix representing an image, rotate the image
                by 90 degrees clockwise. You have to rotate the image in-place.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75 font-mono">
                      {tc.matrix.length > 0
                        ? tc.matrix.map((row) => `[${row.join(",")}]`).join(" ")
                        : "[]"}
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                placeholder="Custom matrix: [[1,2],[3,4]]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>
            <div className="flex justify-center p-2">
              {currentStepData &&
              currentStepData.matrix &&
              currentStepData.matrix.length > 0 ? (
                <div
                  className="grid gap-1"
                  style={{
                    gridTemplateColumns: `repeat(${
                      currentStepData.matrix[0]?.length || 1
                    }, minmax(0, 1fr))`,
                  }}
                >
                  {currentStepData.matrix.map((row, r) =>
                    row.map((cell, c) => {
                      const { pointers, phase } = currentStepData;
                      let baseClass = "bg-gray-100 border-gray-300";

                      if (phase === "transpose") {
                        const isSwap1 =
                          pointers.swap1?.r === r && pointers.swap1?.c === c;
                        const isSwap2 =
                          pointers.swap2?.r === r && pointers.swap2?.c === c;
                        if (isSwap1 || isSwap2) {
                          baseClass =
                            "bg-purple-300 border-purple-500 scale-110 animate-pulse";
                        }
                      } else if (phase === "reverse") {
                        if (pointers.row === r) {
                          const isLeft = pointers.left === c;
                          const isRight = pointers.right === c;
                          if (isLeft || isRight) {
                            baseClass = "bg-blue-300 border-blue-500 scale-110";
                          } else {
                            baseClass = "bg-orange-100 border-orange-300";
                          }
                        }
                      }

                      return (
                        <div
                          key={`${r}-${c}`}
                          className={`w-10 h-10 flex items-center justify-center border-2 rounded transition-all duration-300 font-mono text-sm font-bold ${baseClass}`}
                        >
                          {cell}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Optimal Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The most efficient in-place strategy involves two main steps.
              First, the matrix is transposed along its main diagonal (top-left
              to bottom-right). Second, each row of the transposed matrix is
              reversed. This combination results in a 90-degree clockwise
              rotation.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Time Complexity:</span> O(N^2)
              </div>
              <div>
                <span className="font-medium">Space Complexity:</span> O(1)
              </div>
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Transposing flips the matrix over its diagonal. For example,
                  row `i` becomes column `i`.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Reversing each row after transposition completes the rotation.
                  Try it on paper to see how it works!
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  For anti-clockwise rotation, you can transpose and then
                  reverse each *column*.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-300 border-2 border-purple-500 rounded"></div>
                <span>Transpose Swap</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-100 border-2 border-orange-300 rounded"></div>
                <span>Row being reversed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-300 border-2 border-blue-500 rounded"></div>
                <span>Reverse Swap</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RotateMatrixVisualizer;
